import BottomSheet from '@gorhom/bottom-sheet';
import DownloadPDFIcon from '@assets/icons/file-pdf.svg';
import ExportIcon from '@assets/icons/share-export.svg';
import i18n from '@/utils/i18n';
import React, { useEffect, useRef } from 'react';
import {
  AffordanceHandleIndicator,
  BottomSheetBackdrop,
} from '@/components/bottom-sheet-backdrop';
import { Analytics } from '@/utils/analytics';
import { BaseActionSheet } from '@/components/base-action-sheet';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import {
  Keyboard,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useBottomSheetBackHandler } from '@/hooks/use-bottom-sheet-back-handler';
import {
  useGetOpenPriceQuotePDF,
  useGetPublicLinkPriceQuoteCSV,
} from '@/hooks/use-get-open-price-quote';
import { useLayoutContext } from '@/contexts/layout-context';
import { useOrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  isVisible: boolean;
  onClose: () => void;
  renderInDraggableSheet: boolean;
}

export const SharePriceQuoteActionSheet: React.FC<Props> = ({
  isVisible,
  onClose,
  renderInDraggableSheet,
}) => {
  const bottomSheetRef = useRef<BottomSheet>(null);
  const { selectedCollection } = useOrderGuideItemsContext();
  const { user } = useAuthenticatedUserContext();
  const { isSmallLayout } = useLayoutContext();
  const { handleSheetPositionChange } =
    useBottomSheetBackHandler(bottomSheetRef);
  const { showErrorToast } = useToasts();

  const exportOptions = [
    { title: i18n.t('generate_pdf'), value: 'pdf' },
    { title: i18n.t('export_csv'), value: 'csv' },
  ];

  // Always close keyboard when action sheet its mounted and open
  // Chose not to use `isKeyboardVisible` from useKeyboardVisibility() since it was returning false
  Keyboard.dismiss();

  useEffect(() => {
    /* Implemented this onDismount routine to ensure bottomSheet
     * is not kept opened when it's slid down but remains open,
     * and then cannot be popped up again
     */
    () => {
      bottomSheetRef.current?.close();
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      bottomSheetRef.current?.expand();
    }
  }, [isVisible]);

  const { openPriceQuotePDF } = useGetOpenPriceQuotePDF();
  const { exportPriceQuoteCSV } = useGetPublicLinkPriceQuoteCSV();

  const content = (
    <>
      <Text
        style={[
          TYPOGRAPHY.h2,
          FONTS.primarySemiBold,
          styles.title,
          !renderInDraggableSheet ? styles.titleBaseSheet : null,
        ]}
      >
        {i18n.t('create_price_list')}
      </Text>
      {exportOptions.map((option) => (
        <TouchableOpacity
          key={option.value}
          onPress={async () => {
            if (option.value === 'pdf') {
              // download pdf
              try {
                // Fetch the price quote details and redirect to PMC link
                if (selectedCollection) {
                  await openPriceQuotePDF(selectedCollection.uuid, user.uuid);

                  Analytics.track(
                    'share_price_quote_action_sheet.download_pdf_action.press',
                    {
                      orderGuideRestaurantCollectionUUID:
                        selectedCollection.uuid,
                    },
                  );
                } else {
                  throw Error('No Order Guide has been defined');
                }
              } catch (e) {
                showErrorToast();
              }
            } else if (option.value === 'csv') {
              // export csv
              try {
                // Fetch the price quote details key and download the CSV
                if (selectedCollection) {
                  await exportPriceQuoteCSV(selectedCollection.uuid, user.uuid);

                  Analytics.track(
                    'share_price_quote_action_sheet.export_csv_action.press',
                    {
                      orderGuideRestaurantCollectionUUID:
                        selectedCollection.uuid,
                    },
                  );
                } else {
                  throw Error('No Order Guide has been defined');
                }
              } catch (e) {
                showErrorToast();
              }
            }

            onClose();
          }}
          style={styles.listItem}
        >
          {option.value === 'pdf' ? (
            <DownloadPDFIcon width={24} />
          ) : (
            <ExportIcon width={24} />
          )}
          <Text
            style={[TYPOGRAPHY.h3, FONTS.primaryRegular, styles.listItemText]}
          >
            {option.title}
          </Text>
        </TouchableOpacity>
      ))}
    </>
  );

  return renderInDraggableSheet ? (
    <Modal
      animationType="fade"
      onRequestClose={onClose}
      transparent
      visible={isVisible}
    >
      <GestureHandlerRootView style={styles.gestureHandler}>
        <BottomSheet
          android_keyboardInputMode="adjustResize"
          backdropComponent={BottomSheetBackdrop}
          enableContentPanningGesture={false}
          enablePanDownToClose
          handleComponent={AffordanceHandleIndicator}
          index={1}
          keyboardBehavior="extend"
          keyboardBlurBehavior="restore"
          onChange={handleSheetPositionChange}
          onClose={onClose}
          ref={bottomSheetRef}
          snapPoints={['16%', '26%']}
        >
          <View style={styles.modalContent}>{content}</View>
        </BottomSheet>
      </GestureHandlerRootView>
    </Modal>
  ) : (
    <BaseActionSheet
      contentStyle={[
        styles.modalContainer,
        isSmallLayout ? styles.webMobileMaxHeight : null,
      ]}
      isVisible={isVisible}
      onClose={onClose}
    >
      <ScrollView contentContainerStyle={styles.modalContentBaseSheet}>
        {content}
      </ScrollView>
    </BaseActionSheet>
  );
};

const styles = StyleSheet.create({
  gestureHandler: {
    flex: 1,
  },
  listItem: {
    flexDirection: 'row',
  },
  listItemText: {
    marginLeft: 16,
  },
  modalContainer: {
    paddingBottom: 54,
    paddingHorizontal: 8,
  },
  modalContent: {
    gap: 40,
    paddingHorizontal: 16,
    paddingTop: 0,
  },
  modalContentBaseSheet: {
    gap: 32,
    marginTop: -6,
    paddingHorizontal: 16,
    paddingTop: 0,
  },
  title: {
    marginBottom: -8,
    marginTop: 4,
    textAlign: 'left',
  },
  titleBaseSheet: {
    marginBottom: -4,
  },
  webMobileMaxHeight: {
    maxHeight: '80%', // To keep all of mobile action sheet on screen
  },
});
